<template>
  <div class="-mx-[1.5rem] mt-[16px]">
    <b-table
      :items="participant"
      :fields="tableFields"
      :empty-text="emptyTextLabel"
      primary-key="id"
      responsive
      show-empty
      small
      sticky-header
      class="!max-h-[calc(100vh-330px)] !mb-0"
      :tbody-tr-class="{'hover:bg-slate-100 transition-colors duration-300': participant.length, 'cursor-pointer': participant.length && !disabled}"
    >
      <template #cell(no)="{ index }">
        {{ index + 1 }}
      </template>
      <template #cell(name)="{ item }">
        <div class="flex flex-col gap-[10px]">
          <span class="flex items-center gap-[4px]">
            {{ item.name || '-' }}
            <span
              v-if="statusPillConfig(item.creator_status || item.status).label"
              class="w-fit py-[0px] px-[10px] rounded-full border-[1px] text-[12px] font-medium rounded-full px-[4px] py-[2px] border-[1px] mb-0"
              :class="statusPillConfig(item.creator_status || item.status).class"
            >{{ statusPillConfig(item.creator_status || item.status).label }}</span>
          </span>
          <span class="text-[12px] text-[#828282]">
            {{ item.tiktok_username || '-' }}
          </span>
        </div>
      </template>
      <template #cell(category)="{ value }">
        <category-pill :category="value" />
      </template>
      <template #cell(skala)="{ item, value }">
        <scala-cell
          :scala="value"
          :hover-details="item.hover_details"
          :item-id="item.id"
        />
      </template>
      <template #cell(status)="{ item, index }">
        <status-cell
          :ref="`status-${index}`"
          :data="item"
          :disabled="disabled"
          @need-updated="value => handleStatusUpdate(value, item.id, index)"
        />
      </template>
      <template #cell(uploaded)="{ item, index }">
        <uploaded-cell
          :ref="`uploaded-${index}`"
          :data="item"
          :content-amount="contentAmount"
          :disabled="disabled"
          @need-updated="value => handleCreatorActivity(value, item.id, index, 'uploaded')"
        />
      </template>

      <template #cell(sample)="{ item, index }">
        <sample-cell
          :ref="`sample-${index}`"
          :data="item"
          :disabled="disabled"
          type="sample"
          :options="CREATOR_CAMPAIGN_SAMPLE_STATUS_OPTIONS"
          :status-config="CREATOR_CAMPAIGN_SAMPLE_STATUS"
          :formatter="(value) => ({ sample: value })"
          @need-updated="value => handleCreatorActivity(value, item.id, index, 'sample')"
        />
      </template>
      <template #cell(reimbursement)="{ item, index }">
        <sample-cell
          :ref="`reimbursement-${index}`"
          :data="item"
          :disabled="disabled"
          type="reimbursement"
          :options="CREATOR_CAMPAIGN_REIMBURSEMENT_STATUS_OPTIONS"
          :status-config="CREATOR_CAMPAIGN_REIMBURSEMENT_STATUS"
          :formatter="(value) => ({ reimbursement: value })"
          @need-updated="value => handleCreatorActivity(value, item.id, index, 'reimbursement')"
        />
      </template>
      <template #cell(paid_off)="{ item, index }">
        <sample-cell
          :ref="`paid_off-${index}`"
          :data="item"
          :disabled="disabled"
          type="paid_off"
          :options="CREATOR_CAMPAIGN_PAID_OFF_STATUS_OPTIONS"
          :status-config="CREATOR_CAMPAIGN_PAID_OFF_STATUS"
          :formatter="(value) => ({ paid_off: value })"
          @need-updated="value => handleCreatorActivity(value, item.id, index, 'paid_off')"
        />
      </template>
      <template #head(checkbox)>
        <b-form-checkbox
          :disabled="!participant.length || disabled"
          :checked="allSelected"
          @change="toggleSelectAll"
        />
      </template>
      <template #cell(checkbox)="{ item, index }">
        <b-form-checkbox
          :checked="!!selectedRow[index]"
          :disabled="disabled"
          @change="toggleRow(index, item)"
        />
      </template>
      <template #empty="{emptyText}">
        <empty-table-card
          :fetcher="{
            isLoading: loading,
            error,
            fetchData: () => this.$emit('refresh-data'),
          }"
          :label="emptyText"
        />
      </template>
    </b-table>
  </div>
</template>

<script>
import {
  CAMPAIGN_DETAIL_TABLE_FIELDS,
  CREATOR_CAMPAIGN_SAMPLE_STATUS,
  CREATOR_CAMPAIGN_SAMPLE_STATUS_OPTIONS,
  CREATOR_CAMPAIGN_REIMBURSEMENT_STATUS,
  CREATOR_CAMPAIGN_REIMBURSEMENT_STATUS_OPTIONS,
  CREATOR_CAMPAIGN_PAID_OFF_STATUS,
  CREATOR_CAMPAIGN_PAID_OFF_STATUS_OPTIONS,
} from '../config'
import { CREATOR_STATUS_STYLE } from '../../creator-management/config'
import EmptyTableCard from '../../creator-management/EmptyTableCard.vue'
import StatusCell from './StatusCell.vue'
import SampleCell from './SampleCell.vue'
import UploadedCell from './UploadedCell.vue'
import CategoryPill from '../../creator-management/CategoryPill.vue'
import ScalaCell from './ScalaCell.vue'

export default {
  name: 'TableComponent',
  components: {
    EmptyTableCard,
    StatusCell,
    SampleCell,
    UploadedCell,
    CategoryPill,
    ScalaCell,
  },
  props: {
    participant: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: true,
    },
    error: {
      type: String,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    contentAmount: {
      type: Number,
      default: 0,
    },
    phase: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      CAMPAIGN_DETAIL_TABLE_FIELDS,
      CREATOR_CAMPAIGN_SAMPLE_STATUS,
      CREATOR_CAMPAIGN_SAMPLE_STATUS_OPTIONS,
      CREATOR_CAMPAIGN_REIMBURSEMENT_STATUS,
      CREATOR_CAMPAIGN_REIMBURSEMENT_STATUS_OPTIONS,
      CREATOR_CAMPAIGN_PAID_OFF_STATUS,
      CREATOR_CAMPAIGN_PAID_OFF_STATUS_OPTIONS,
      drawerData: null,
      selectedRow: {},
      selectedCount: 0,
    }
  },
  computed: {
    effectivePhase() {
      if (this.phase) return this.phase
      return this.$route?.query?.tab === 'ongoing' ? 'ongoing' : 'pre_campaign'
    },
    tableFields() {
      return CAMPAIGN_DETAIL_TABLE_FIELDS(this.effectivePhase)
    },
    emptyTextLabel() {
      if (this.loading) return 'Memuat data...'
      if (this.error) return 'Terdapat kesalahan saat memuat data'
      return 'Tidak ada data yang ditampilkan.'
    },
    allSelected() {
      return this.participant?.length > 0
        && this.selectedCount === this.participant?.length
    },
  },
  methods: {
    toggleSelectAll(checked) {
      if (this.disabled) return
      const data = this.participant
      if (checked && data?.length) {
        const selected = {}
        data.forEach((item, index) => { selected[index] = item })
        this.selectedRow = selected
        this.selectedCount = data.length
      } else {
        this.selectedRow = {}
        this.selectedCount = 0
      }
    },
    toggleRow(index, item) {
      if (this.disabled) return
      if (this.selectedRow[index]) {
        this.$delete(this.selectedRow, index)
        this.selectedCount = Math.max(0, this.selectedCount - 1)
      } else if (!this.selectedRow[index]) {
        this.$set(this.selectedRow, index, item)
        this.selectedCount += 1
      }
    },
    parseScala(value) {
      if (!value) return ['N', 'A']
      return value.split('/')
    },
    /**
     * Handles the status update for participants.
     * If the updated row is part of a selection (checkbox checked), it applies the
     * status update to all currently selected rows. Otherwise, it updates only the single row.
     * It also manages the loading state and values of the child StatusCell components via $refs.
     *
     * @param {Object} event - The status update event data
     * @param {string} event.value - New status value
     * @param {string} event.oldValue - Old status value (for rollback)
     * @param {number|string} id - Participant ID
     * @param {number} index - Row index
     */
    async handleStatusUpdate({ value, oldValue }, id, index) {
      if (this.disabled) return
      const payload = {
        participant_ids: [id],
        suitability_status: value,
      }
      const updatedParticipants = this.selectedRow[index] ? Object.entries(this.selectedRow) : [[index, { id }]]
      if (updatedParticipants.length > 0) {
        payload.participant_ids = updatedParticipants.map(([originalIndex, item]) => {
          if (this.$refs[`status-${originalIndex}`]) {
            this.$refs[`status-${originalIndex}`].setLoading(true)
          }
          return item.id
        })
      }
      let isError = false
      try {
        await this.$http_new.put(`/komclass/api/v1/komlabs/campaigns/${this.$route.params.id}/participants/suitability`, payload)
        updatedParticipants.forEach(([originalIndex]) => {
          const ref = this.$refs[`status-${originalIndex}`]
          if (!ref) return
          ref.setLoading(false)
          ref.setValue(value)
        })
      } catch (error) {
        isError = true
        updatedParticipants.forEach(([originalIndex]) => {
          const ref = this.$refs[`status-${originalIndex}`]
          if (!ref) return
          ref.setLoading(false)
          ref.setValue(oldValue)
        })
        this.$toast_error({ message: 'Gagal memperbarui status creator. Silakan coba lagi.' })
      }
    },
    /**
     * Handles the activity tracking update for participants (e.g., sample, reimbursement, paid_off).
     *
     * @param {Object} event - The activity update event data
     * @param {string|boolean|number} event.value - New activity status primitive value
     * @param {string|boolean|number} event.oldValue - Old activity status primitive value (for rollback on failure)
     * @param {number|string} id - The primary participant ID being updated
     * @param {number} index - The row index in the table
     * @param {string} type - The specific activity type being updated (e.g., 'sample', 'reimbursement', 'paid_off')
     */
    async handleCreatorActivity({ value: originalValue, oldValue: originalOldValue }, id, index, type) {
      if (this.disabled) return
      const { value, oldValue } = {
        value: { [type]: originalValue },
        oldValue: { [type]: originalOldValue },
      }
      const payload = {
        participant_ids: [id],
        ...value,
      }
      const updatedParticipants = this.selectedRow[index] ? Object.entries(this.selectedRow) : [[index, { id }]]
      if (updatedParticipants.length > 0) {
        payload.participant_ids = updatedParticipants.map(([originalIndex, item]) => {
          if (this.$refs[`${type}-${originalIndex}`]) {
            this.$refs[`${type}-${originalIndex}`].setLoading(true)
          }
          return item.id
        })
      }
      let isError = false
      try {
        await this.$http_new.put(`/komclass/api/v1/komlabs/campaigns/${this.$route.params.id}/participants/tracking`, payload)
        updatedParticipants.forEach(([originalIndex]) => {
          const ref = this.$refs[`${type}-${originalIndex}`]
          if (!ref) return
          ref.setLoading(false)
          ref.setValue(value[type])
        })
      } catch (error) {
        isError = true
        updatedParticipants.forEach(([originalIndex]) => {
          const ref = this.$refs[`${type}-${originalIndex}`]
          if (!ref) return
          ref.setLoading(false)
          ref.setValue(oldValue[type])
        })
        this.$toast_error({ message: 'Gagal memperbarui data. Silakan coba lagi.' })
      }
    },
    /**
     * Konfigurasi tampilan pill status
     * @param {string} status
     * @returns {{ label: string, class: string }}
     */
    statusPillConfig(status) {
      return CREATOR_STATUS_STYLE[status?.toLowerCase()] || {
        label: '',
        class: 'border-none',
      }
    },
  },
}
</script>
